<template>
	<div>
		<auth-container :page="page" v-if="$route.name === 'SrPlanEdit'">
			<h2 class="mb-4">Budget Plan for Application #{{application.appNum}}</h2>

			<b-tabs v-model="page.tabIndex" content-class="mt-4" class="page-nav-tabs">
				<b-tab title="Overview">
					<b-row>
						<b-col md>
							<b-table-simple small class="table-definitions">
								<b-tbody>
									<b-tr>
										<b-th>Dam</b-th>
										<b-td>
											<span v-if="isNullOrEmpty(application.damId)">N/A</span>
											<router-link v-else :to="`/dams/${application.damId}`">{{dam.name}}</router-link>
										</b-td>
									</b-tr>
									<b-tr>
										<b-th>Funding Cycle</b-th>
										<b-td>{{valueOrNa(item.fundCycle)}}</b-td>
									</b-tr>
									<b-tr>
										<b-th>Date Budget Performed</b-th>
										<b-td>{{item.performedDate | date('ddd, MMM D, YYYY', 'N/A')}}</b-td>
									</b-tr>
								</b-tbody>
							</b-table-simple>
						</b-col>
					</b-row>

					<b-table-simple small class="table-definitions" bordered>
						<b-tbody>
							<b-tr class="bg-light">
								<b-th></b-th>
								<b-th class="text-right">Design</b-th>
								<b-th class="text-right">Land Rights</b-th>
								<b-th class="text-right">Sponsor</b-th>
								<b-th class="text-right">Total</b-th>
							</b-tr>
							<b-tr>
								<b-th class="bg-light">Federal Reimbursement</b-th>
								<b-td class="text-right">{{money(item.designFederalReimbursement)}}</b-td>
								<b-td class="text-right">{{money(item.landRightsFederalReimbursement)}}</b-td>
								<b-td class="text-right">{{money(item.constructionFederalReimbursement)}}</b-td>
								<b-td class="bg-light text-right">{{money(planTotals.federalSubtotal)}}</b-td>
							</b-tr>
							<b-tr>
								<b-th class="bg-light">State Cost</b-th>
								<b-td class="text-right">{{money(item.designStateCost)}}</b-td>
								<b-td class="text-right">{{money(item.landRightsStateCost)}}</b-td>
								<b-td class="text-right">{{money(item.constructionStateCost)}}</b-td>
								<b-td class="bg-light text-right">{{money(planTotals.stateSubtotal)}}</b-td>
							</b-tr>
							<b-tr>
								<b-th class="bg-light">Sponsor Cost</b-th>
								<b-td class="text-right">&mdash;</b-td>
								<b-td class="text-right">{{money(item.landRightsSponsorsCost)}}</b-td>
								<b-td class="text-right">{{money(item.constructionSponsorsCost)}}</b-td>
								<b-td class="bg-light text-right">{{money(planTotals.sponsorsSubtotal)}}</b-td>
							</b-tr>
							<b-tr class="bg-light">
								<b-th>Total</b-th>
								<b-td class="text-right">{{money(planTotals.designSubtotal)}}</b-td>
								<b-td class="text-right">{{money(planTotals.landRightsSubtotal)}}</b-td>
								<b-td class="text-right">{{money(planTotals.constructionSubtotal)}}</b-td>
								<b-td class="text-right font-weight-bold">{{money(planTotals.totalCost)}}</b-td>
							</b-tr>
						</b-tbody>
					</b-table-simple>

					<h3 class="mb-3">Application</h3>
					<div>
						<b-table-simple small responsive class="border-bottom">
							<b-thead class="bg-light">
								<b-tr>
									<b-th></b-th>
									<b-th>Date Received</b-th>
									<b-th>Request Type</b-th>
									<b-th>Funding Cycle</b-th>
									<b-th>SLO</b-th>
									<b-th>Primary Name</b-th>
									<b-th>Alternate Name</b-th>
									<b-th>Total Est. Cost</b-th>
								</b-tr>
							</b-thead>
							<b-tbody>
								<b-tr>
									<b-td>
										<router-link :to="`/sr/applications/${itemId}`" title="View/Edit">
											<font-awesome-icon :icon="['fas', 'edit']" />
										</router-link>
									</b-td>
									<b-td>{{application.receivedDate | date('ddd, MMM D, YYYY', 'N/A')}}</b-td>
									<b-td>{{valueOrNa(application.requestType)}}</b-td>
									<b-td>{{valueOrNa(application.fundCycle)}}</b-td>
									<b-td>{{valueOrNa(application.sloAgent)}}</b-td>
									<b-td>{{valueOrNa(application.primaryName)}}</b-td>
									<b-td>{{valueOrNa(application.alternateName)}}</b-td>
									<b-td>{{money(application.totalEstimatedCost)}}</b-td>
								</b-tr>
							</b-tbody>
						</b-table-simple>
					</div>

					<h3 class="mb-3">Project</h3>
					<p v-if="isNullOrEmpty(project)">
						There is no project for this application. <router-link to="/sr/projects/create">Create a project.</router-link>
					</p>
					<div v-else>
						<b-table-simple small responsive class="border-bottom">
							<b-thead class="bg-light">
								<b-tr>
									<b-th></b-th>
									<b-th>TPN</b-th>
									<b-th>Status</b-th>
									<b-th>Category</b-th>
									<b-th>Phase</b-th>
									<b-th>Name</b-th>
								</b-tr>
							</b-thead>
							<b-tbody>
								<b-tr>
									<b-td>
										<router-link :to="`/sr/projects/${itemId}`" title="View/Edit">
											<font-awesome-icon :icon="['fas', 'edit']" />
										</router-link>
									</b-td>
									<b-td>{{project.projectNumber}}</b-td>
									<b-td>{{project.status}}</b-td>
									<b-td>{{project.category}}</b-td>
									<b-td>{{project.phase}}</b-td>
									<b-td>{{project.name}}</b-td>
								</b-tr>
							</b-tbody>
						</b-table-simple>
					</div>

					<fixed-action-bar>
						<b-button v-if="isNullOrEmpty(project) && !isReadOnly" type="button" variant="success" to="/sr/projects/create" class="mr-2">Create Project</b-button>
						<b-button type="button" variant="primary" to="print" append target="_blank" class="mr-2">Print</b-button>
						<back-button />
						<b-button v-if="isAuthorized(roleNames.admin)" type="button" variant="danger" @click="page.delete.show = true" class="ml-auto">Delete</b-button>
					</fixed-action-bar>
				</b-tab>
				<b-tab title="Edit" v-if="!isReadOnly">
					<sr-plan-form ref="itemForm" :item="item" :dam="dam" :application="application" is-update @change="changesSaved"></sr-plan-form>
				</b-tab>
				<b-tab title="Files">
					<file-manager :apiUrl="apiUrl" :files="files" :itemId="itemId" itemLabel="application" @change="filesChanged"></file-manager>
				</b-tab>
				<b-tab title="Change Log">
					<log-viewer db-table="SrPlans" :item-id="itemId"></log-viewer>
				</b-tab>
			</b-tabs>

			<b-modal v-model="page.delete.show" size="md" title="Confirm delete" no-close-on-backdrop no-close-on-esc hide-header-close>
				<error-list :errors="page.delete.errors"></error-list>

				<p>
					Are you sure you want to delete this plan?
					This action is permanent and cannot be undone.
				</p>

				<div slot="modal-footer">
					<save-button type="button" :saving="page.delete.saving" @click.native="confirmDelete" text="Delete" variant="danger" />
					<b-button type="button" variant="secondary" @click="page.delete.show = false" class="ml-1">Cancel</b-button>
				</div>
			</b-modal>
		</auth-container>
		<router-view></router-view>
	</div>
</template>

<script>
	import FileManager from '@/components/FileManager';
	import SrPlanForm from './SrPlanForm.vue';

	export default {
		name: 'SrPlanEdit',
		components: {
			SrPlanForm, FileManager
		},
		data() {
			return {
				apiUrl: 'srbudgetplans',
				listRoute: 'SrPlanList',
				page: {
					errors: [],
					loading: false,
					showLogin: false,
					tabIndex: 0,
					delete: {
						show: false,
						errors: [],
						saving: false
					}
				},
				item: {},
				planTotals: {},
				application: {},
				dam: {},
				project: {},
				files: []
			}
		},
		async created() {
			await this.get();
		},
		watch: {
			'$route': 'get'
		},
		methods: {
			async get() {
				this.page.loading = true;
				this.page.errors = [];

				try {
					const response = await this.$http.get(`${this.apiUrl}/${this.itemId}`, this.getTokenHeader());
					this.log(response.data);
					this.item = response.data.plan;
					this.dam = response.data.dam;
					this.project = response.data.project;
					this.files = response.data.files;
					this.planTotals = response.data.planTotals;
					this.application = response.data.application;
				} catch (error) {
					if (this.isApiUnauthorized(error)) this.page.showLogin = true;
					else this.page.errors = this.logError(error);
				}

				this.page.loading = false;
			},
			async changesSaved() {
				await this.get();
				this.$refs.itemForm.launchToast();
				this.page.tabIndex = 1;
			},
			async filesChanged() {
				await this.get();
				this.page.tabIndex = 2;
			},
			async confirmDelete() {
				this.page.delete.errors = [];
				this.page.delete.saving = true;

				try {
					await this.$http.delete(`${this.apiUrl}/${this.itemId}`, this.getTokenHeader());
					this.page.delete.show = false;
					this.$router.push({ name: this.listRoute });
				} catch (error) {
					if (this.isApiUnauthorized(error)) this.page.showLogin = true;
					else this.page.delete.errors = this.logError(error);
				}

				this.page.delete.saving = false;
			}
		},
		computed: {
			itemId() {
				return this.$route.params.id;
			}
		}
	}
</script>